import { NgClass } from '@angular/common';
import { Component, DestroyRef, Input, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { TranslocoModule } from '@jsverse/transloco';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { EmailChangeFacade } from '@zero/self-service/domain';
import { TransactionAuthorizationComponent } from '@zero/shared/ui-web-components';
import { ButtonComponent, IconComponent } from '@zero/shared/ui-web-design-system';
import { CustomerStateFacade } from '@zero/shared/util-global-state';
import { AuthEmailChange, AuthorizationData, TransactionAuthorizationResult, UserDataModel } from '@zero/shared/util-models';
import { AuthService } from '@zero/shared/util-web-app-services';
import { delay, filter, tap } from 'rxjs';
import { emailFlowState } from './editEmailState';

@Component({
  selector: 'self-service-user-contact-data-email',
  templateUrl: './user-contact-data-email.component.html',
  imports: [NgClass, IconComponent, NgbTooltipModule, TranslocoModule, ButtonComponent, FormsModule, TransactionAuthorizationComponent],
})
export class UserContactDataEmailComponent implements OnInit {
  @Input() public currentUser: UserDataModel;

  readonly #emailChangeFacade = inject(EmailChangeFacade);
  readonly #authService = inject(AuthService);
  readonly #customerStateFacade = inject(CustomerStateFacade);
  readonly #destroyRef = inject(DestroyRef);

  public flowStates = emailFlowState;
  public flowState: emailFlowState;
  public sessionAuthorized = false;
  public newEmail: string;
  public dataToAuthorize: AuthorizationData<AuthEmailChange>;
  public oldEmailVerifCode;
  public newEmailVerifCode;
  public error: string;

  ngOnInit() {
    this.flowState = emailFlowState.VERIFIED;
  }

  public startProcess() {
    this.error = '';
    this.flowState = this.flowStates.NEW_EMAIL_INPUT;
  }

  public saveNewEmail() {
    this.#emailChangeFacade.startProcess(this.newEmail);

    this.#emailChangeFacade.requestId$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((requestId) => {
      if (requestId) {
        this.dataToAuthorize = new AuthorizationData<AuthEmailChange>(new AuthEmailChange());
        this.dataToAuthorize.data.customerId = this.#customerStateFacade.customerBaseData.customerId;
        this.dataToAuthorize.data.emailChangeData.additionalProperties.requestId = requestId;

        this.flowState = this.flowStates.TAN;
        this.error = '';
      }
    });

    this.#emailChangeFacade.selectErrors$
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        filter((e) => e != null),
      )
      .subscribe({
        next: (response) => {
          this.error = response.error?.message ?? '';
        },
      });
  }

  public async authorizedTransactionResult(result: TransactionAuthorizationResult): Promise<void> {
    if (result.success) {
      this.error = '';
      this.sessionAuthorized = true;
      this.flowState = emailFlowState.OLD_EMAIL_VERIFICATION;
      await this.#emailChangeFacade.sendCodeOldEmail();
    } else {
      this.sessionAuthorized = false;
      if (result.reason === 'CanceledByUser') {
        this.flowState = emailFlowState.VERIFIED;
        this.cancelProcess();
      } else if (result.reason) {
        this.error = result.reason;
      }
    }
  }

  #listenForOldEmailVerified() {
    this.#emailChangeFacade.selectOldEmailVerified$
      .pipe(
        filter((verified) => !!verified),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => {
        this.error = '';
        this.flowState = emailFlowState.NEW_EMAIL_VERIFICATION;
        this.#emailChangeFacade.sendCodeNewEmail();
      });
  }
  #listenForOldEmailError() {
    this.#emailChangeFacade.selectOldEmailError$
      .pipe(
        filter((error) => !!error),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => {
        this.error = 'user-self-service.my-data.contact-data.verification-code-invalid';
      });
  }

  #listenForNewEmailVerified() {
    this.#emailChangeFacade.selectNewEmailVerified$
      .pipe(
        filter((verified) => !!verified),
        tap(() => {
          this.error = '';
          this.flowState = emailFlowState.FINISHED;
        }),
        delay(7000),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => this.#authService.explicitLogout());
  }
  #listenForNewEmailError() {
    this.#emailChangeFacade.selectNewEmailError$
      .pipe(
        filter((error) => !!error),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => {
        this.error = 'user-self-service.my-data.contact-data.verification-code-invalid';
      });
  }

  public verificateOldEmail() {
    this.#emailChangeFacade.verifyOldEmail(this.oldEmailVerifCode);

    this.#listenForOldEmailVerified();

    this.#listenForOldEmailError();
  }

  public verificateNewEmail() {
    this.#emailChangeFacade.verifyNewEmail(this.newEmailVerifCode);

    this.#listenForNewEmailVerified();

    this.#listenForNewEmailError();
  }

  public cancelProcess() {
    this.#emailChangeFacade.abortProcess();
    this.error = '';
    this.newEmail = '';
    this.oldEmailVerifCode = '';
    this.newEmailVerifCode = '';

    this.flowState = emailFlowState.VERIFIED;
  }
}
